<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { $router } from '@/router'
import { useTitle } from '@/composables/title'
import { useTranslate } from '@/composables/language'
import { useMenu, useSubmenu, useTopBar } from '@/composables/menu'
import { useSessionTimer } from '@/composables/session'
import { tare } from '@/composables/hardware/weightScale'
import DebugFooter from '@/components/Maintenance/DebugFooter.vue'
import Button from '@/components/Button.vue'

const { t } = useTranslate();

const props = defineProps({
  isApplying: {
    type: Boolean,
    default: false
  },
});

const emit = defineEmits([ "applyEmpty", "applyLoaded", "finish", "cancel" ]);

const steps = [ 'intro', 'empty', 'weight', 'measure' ];
const currentStep = ref(0);
const stepKey = computed(() => steps[currentStep.value]);

const zeroed = ref(false);

// function zeroScale() {
//   tare();
//   zeroed.value = true;
// }

const knownWeight = ref("");
const knownWeightValid = computed(() => parseFloat(knownWeight.value) > 0);

const measuredValue = ref(null);
const stabilizing = ref(false);
const stabilized = ref(false);

function startMeasurement() {
  stabilized.value = false;
  measuredValue.value = null;
  stabilizing.value = true;
  //TODO: read the scale, wait 2-3s for the value to stabilize, then set
  //stabilizing.value = false; stabilized.value = true; measuredValue.value = [g].
}

const canAdvance = computed(() => {
  switch (stepKey.value) {
    //case 'empty': return zeroed.value;
    case 'weight': return knownWeightValid.value > 0;
    default: return true;
  }
});

//const isApplying = ref(false);

let isWaitUntilApplied = false;
function waitUntilApplied() {
  isWaitUntilApplied = true;
}

watch(() => props.isApplying, (value) => {
  console.log("watch isApplying", value)
  if (isWaitUntilApplied && value === false) {
      console.log("watch isApplying go next step")
    isWaitUntilApplied = false;
    currentStep.value++;
  }
});

function nextStep() {
  if (!canAdvance.value) return;

  if (currentStep.value < steps.length - 1) {

    if (stepKey.value === "empty") {
      applyEmpty();
      waitUntilApplied();
    }
    else if (stepKey.value === "weight") {
      applyLoaded();
      waitUntilApplied();
    }
    else {
        currentStep.value++;

        if (stepKey.value === "measure") {
        //startMeasurement();
        }
    }
  }
}

function prevStep() {
  if (currentStep.value > 0) {
    currentStep.value--;
  }
}

function applyEmpty() {
  emit("applyEmpty");
}

function applyLoaded() {
  emit("applyLoaded", { knownWeight: parseInt(knownWeight.value) });
}

function finish() {
  //$router.back();
  emit("finish");
}

function cancel() {
  //$router.back();
  emit("cancel");
}

onMounted(() => {
});
</script>

<template>
    <div class="w-full min-h-[800px] flex flex-col">

        <!-- Step indicator -->
        <div class="w-full max-w-2xl mx-auto pt-12 px-4">
            <div class="flex items-center justify-center space-x-2">
                <template v-for="(step, i) in steps" :key="step">
                    <div class="flex items-center">
                        <div class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors"
                            :class="{
                                'bg-blue-600 text-white': i === currentStep,
                                'bg-green-500 text-white': i < currentStep,
                                'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400': i > currentStep,
                            }">
                            <i class="fa fa-check" v-if="i < currentStep"></i>
                            <span v-else>{{ i + 1 }}</span>
                        </div>
                        <span class="ml-2 text-sm hidden sm:inline" :class="{
                            'text-blue-600 font-medium dark:text-blue-400': i === currentStep,
                            'text-green-600 dark:text-green-400': i < currentStep,
                            'text-gray-400 dark:text-gray-500': i > currentStep,
                        }">
                            {{ t(`calibration.step_${step}`) }}
                        </span>
                    </div>
                    <div class="w-12 h-0.5 bg-gray-300 dark:bg-gray-600" v-if="i < steps.length - 1"
                        :class="{ 'bg-green-500 dark:bg-green-500': i < currentStep }"></div>
                </template>
            </div>
        </div>

        <!-- Content area -->
        <div class="flex-1 flex items-center justify-center px-4 py-8">
            <div class="w-full max-w-xl">

                <!-- Intro -->
                <div v-if="stepKey === 'intro'" class="text-center">
                    <div class="mb-6">
                        <i class="fa fa-scale-balanced text-blue-500" style="font-size: 64px;"></i>
                    </div>
                    <h1 class="text-3xl font-bold mb-4 text-gray-900 dark:text-white">{{ t('calibration.intro_title') }}</h1>
                    <p class="text-lg text-gray-600 dark:text-gray-300 mb-8">{{ t('calibration.intro_text') }}</p>
                </div>

                <!-- Empty scale -->
                <div v-if="stepKey === 'empty'" class="text-center">
                    <div class="mb-6">
                        <i class="fa fa-scale-balanced text-blue-500" style="font-size: 64px;"></i>
                    </div>
                    <h1 class="text-3xl font-bold mb-4 text-gray-900 dark:text-white">{{ t('calibration.empty_title') }}</h1>

                    <p class="text-gray-600 dark:text-gray-300 mb-6">{{ t('calibration.empty_text') }}</p>

                    <!-- <div v-if="!zeroed">
                        <Button type="primary" @click="zeroScale()">
                            <i class="fa fa-arrows-to-dot mr-2"></i>{{ t('calibration.empty_zero') }}
                        </Button>
                    </div>
                    <p class="text-green-600 font-medium" v-else>
                        <i class="fa fa-circle-check mr-2"></i>{{ t('calibration.empty_zeroed') }}
                    </p> -->

                    <p class="text-sm text-gray-500 dark:text-gray-400 mt-6">{{ t('calibration.empty_hint') }}</p>

                    <div class="grid w-full mt-8 place-items-center" :class="{ 'invisible': !isApplying }">
                        <Spinner size="9" />
                    </div>
                </div>

                <!-- Reference weight -->
                <div v-if="stepKey === 'weight'" class="text-center">
                    <!-- <div class="flex flex-col items-center mb-6">
                        <i class="fa fa-weight text-blue-500" style="font-size: 44px;"></i>
                        <i class="fa fa-scale-balanced text-blue-500 -mt-1" style="font-size: 64px;"></i>
                    </div> -->
                    <div class="mb-6">
                        <i class="fa fa-scale-balanced text-blue-500" style="font-size: 64px;"></i>
                    </div>
                    <h1 class="text-3xl font-bold mb-4 text-gray-900 dark:text-white">{{ t('calibration.weight_title') }}</h1>

                    <p class="text-gray-600 dark:text-gray-300 mb-6">{{ t('calibration.weight_text') }}</p>

                    <!-- <div class="flex flex-col items-center mb-6">
                        <i class="fa fa-weight text-blue-500" style="font-size: 44px;"></i>
                        <i class="fa fa-scale-balanced text-blue-500 -mt-1" style="font-size: 96px;"></i>
                    </div> -->

                    <div class="flex items-center justify-center gap-2 max-w-xs mx-auto">
                        <!-- <input type="number" inputmode="decimal" min="0" step="any" v-model="knownWeight"
                            class="cw-input" :placeholder="t('calibration.weight_placeholder')"
                            @keydown.enter="knownWeightValid && nextStep()" /> -->
                        <TextBox type="numeric" inputmode="decimal" min="0" step="any" v-model="knownWeight"
                            class="cw-input" :placeholder="t('calibration.weight_placeholder')" :enabled="!isApplying"
                            @keydown.enter="knownWeightValid && nextStep()" />
                        <span class="text-gray-500 dark:text-gray-400 text-lg">{{ t('calibration.weight_unit') }}</span>
                    </div>

                    <!-- <p class="text-sm text-gray-500 dark:text-gray-400 mt-6">{{ t('calibration.weight_hint') }}</p> -->

                    <div class="grid w-full mt-8 place-items-center" :class="{ 'invisible': !isApplying }">
                        <Spinner size="9" />
                    </div>

                </div>

                <!-- Measurement -->
                <div v-if="stepKey === 'measure'" class="text-center">
                    <div class="mb-6">
                        <i class="fa fa-scale-balanced text-blue-500" style="font-size: 64px;"></i>
                    </div>
                    <h1 class="text-3xl font-bold mb-4 text-gray-900 dark:text-white">{{ t('calibration.measure_title') }}</h1>
                    <p class="text-gray-600 dark:text-gray-300 mb-6">{{ t('calibration.measure_text') }}</p>

                    <!-- Equalizer-style bars: animate while stabilizing, settle to full when stable -->
                    <!-- <div class="flex items-end justify-center gap-2 h-32 mb-4 mx-auto" style="max-width: 200px;"
                        :class="{ 'cw-active': stabilizing, 'cw-done': stabilized }">
                        <div class="cw-bar" v-for="n in 5" :key="n"></div>
                    </div> -->

                    <!-- Value -->
                    <!-- <div class="font-mono text-4xl text-green-500 tabular-nums mb-2" v-if="measuredValue !== null">
                        {{ measuredValue }} <span class="text-2xl text-green-500/70">{{ t('calibration.measure_unit')
                            }}</span>
                    </div> -->

                    <!-- Status -->
                    <!-- <p class="text-blue-600 dark:text-blue-400 font-medium" v-if="stabilizing">
                        <i class="fa fa-wave-square mr-2"></i>{{ t('calibration.measure_stabilizing') }}
                    </p>
                    <p class="text-green-600 font-medium" v-else-if="stabilized">
                        <i class="fa fa-circle-check mr-2"></i>{{ t('calibration.measure_stable') }}
                    </p>
                    <p class="text-gray-500 dark:text-gray-400" v-else>
                        {{ t('calibration.measure_place') }}
                    </p>

                    <Button type="secondary" class="mt-4" @click="startMeasurement()" v-if="!stabilizing">
                        <i class="fa fa-rotate-right mr-2"></i>{{ t('calibration.measure_start') }}
                    </Button> -->
                </div>

            </div>
        </div>

        <!-- Navigation buttons -->
        <div class="w-full max-w-xl mx-auto px-0 pb-6">
            <div class="flex justify-between">
                <!-- <Button type="secondary" @click="prevStep()" v-if="currentStep > 0">
                    <i class="fa fa-arrow-left mr-2"></i>{{ t('calibration.previous') }}
                </Button> -->
                <Button type="secondary" @click="cancel()">
                    {{ t('calibration.cancel') }}
                </Button>

                <!-- Next button — disabled until the current step's value is set (zeroed / known weight) -->
                <Button type="primary" @click="nextStep()" v-if="currentStep < steps.length - 1" :enabled="canAdvance && !isApplying">
                    {{ t('calibration.next') }}<i class="fa fa-arrow-right ml-2"></i>
                </Button>

                <!-- Finish button on last step -->
                <Button type="primary" @click="finish()" v-else>
                    <i class="fa fa-check mr-2"></i>{{ t('calibration.finish') }}
                </Button>

            </div>
        </div>

    </div>
</template>

<style scoped>
.cw-input {
    @apply bg-gray-50 border border-gray-300 text-gray-900 text-lg rounded-lg focus:ring-blue-500 focus:border-blue-500 w-40 p-2.5 text-center dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white;
}

.cw-bar {
    width: 18px;
    height: 100%;
    border-radius: 6px;
    transform-origin: bottom;
    transform: scaleY(0.3);
    opacity: 0.45;
    background: linear-gradient(to top, rgb(34 197 94), rgb(22 163 74));
    transition: transform 0.3s ease, opacity 0.3s ease;
}

.cw-active .cw-bar {
    opacity: 1;
    animation: cw-bar 1.2s ease-in-out infinite;
}

.cw-active .cw-bar:nth-child(1) {
    animation-delay: 0s;
}

.cw-active .cw-bar:nth-child(2) {
    animation-delay: 0.15s;
}

.cw-active .cw-bar:nth-child(3) {
    animation-delay: 0.3s;
}

.cw-active .cw-bar:nth-child(4) {
    animation-delay: 0.45s;
}

.cw-active .cw-bar:nth-child(5) {
    animation-delay: 0.6s;
}

.cw-done .cw-bar {
    transform: scaleY(1);
    opacity: 1;
}

@keyframes cw-bar {

    0%,
    100% {
        transform: scaleY(0.3);
    }

    50% {
        transform: scaleY(1);
    }
}

@media (prefers-reduced-motion: reduce) {
    .cw-active .cw-bar {
        animation: none;
        transform: scaleY(0.6);
    }
}
</style>
